/**
 * Enhanced GuestList Component with virtualization and memoization
 * 
 * Displays a list of podcast guests with filtering, sorting, and virtualized rendering
 * for improved performance with large datasets.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useGuests } from './useGuests';
import { Guest, GuestStatus } from '@/types/models/schema';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  RefreshCw, 
  Filter,
  ArrowUpDown,
  Plus
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';

// Define sort value types
type SortableValue = string | number | null | undefined;

/**
 * Sort data based on field and direction
 * 
 * @param a First item to compare
 * @param b Second item to compare
 * @param field Field to sort by
 * @param direction Sort direction ('asc' or 'desc')
 * @returns Sort comparison result
 */
const sortData = <T extends Record<string, unknown>>(
  a: T,
  b: T,
  field: keyof T,
  direction: 'asc' | 'desc'
): number => {
  const aValue = a[field] as SortableValue;
  const bValue = b[field] as SortableValue;
  
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return direction === 'asc' 
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  }
  
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return direction === 'asc' 
      ? aValue - bValue
      : bValue - aValue;
  }
  
  // Handle null/undefined values
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return direction === 'asc' ? -1 : 1;
  if (bValue == null) return direction === 'asc' ? 1 : -1;
  
  return 0;
};

// Status badge component (memoized)
const StatusBadge: React.FC<{ status: GuestStatus }> = React.memo(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'recorded':
        return 'bg-blue-100 text-blue-800';
      case 'published':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
      {status}
    </span>
  );
});

StatusBadge.displayName = 'StatusBadge';

// Guest row component (memoized)
interface GuestRowProps {
  guest: Guest;
  style?: React.CSSProperties;
}

const GuestRow = React.memo(({ guest, style }: GuestRowProps) => {
  const navigate = useNavigate();
  
  const handleClick = useCallback(() => {
    navigate(`${ROUTES.GUESTS}/${guest.id}`);
  }, [navigate, guest.id]);
  
  return (
    <div 
      className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer" 
      style={style}
      onClick={handleClick}
    >
      <div className="grid grid-cols-12 px-6 py-4">
        <div className="col-span-3">
          <div className="text-sm font-medium text-gray-900">{guest.name}</div>
          <div className="text-sm text-gray-500">{guest.email}</div>
        </div>
        <div className="col-span-3">
          <div className="text-sm text-gray-900">{guest.company || ''}</div>
          <div className="text-sm text-gray-500">{guest.title || ''}</div>
        </div>
        <div className="col-span-2">
          <div className="text-sm text-gray-900">{guest.episode_number || 'TBD'}</div>
        </div>
        <div className="col-span-2">
          <StatusBadge status={guest.status} />
        </div>
        <div className="col-span-2 text-right text-sm font-medium">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${ROUTES.GUESTS}/${guest.id}/edit`);
            }}
            className="text-primary-600 hover:text-primary-900 mr-4"
          >
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${ROUTES.GUESTS}/${guest.id}/schedule`);
            }}
            className="text-green-600 hover:text-green-900"
          >
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
});

GuestRow.displayName = 'GuestRow';

const GuestList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Guest>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<GuestStatus | ''>('');
  
  const { 
    guests, 
    isLoading, 
    error,
    refetchGuests,
    retryFetch
  } = useGuests();

  // Handle search input change (memoized)
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Handle status filter change (memoized)
  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value as GuestStatus | '');
  }, []);

  // Handle add new guest (memoized)
  const handleAddGuest = useCallback(() => {
    navigate(ROUTES.GUEST_NEW);
  }, [navigate]);

  // Handle retry (memoized)
  const handleRetry = useCallback(() => {
    retryFetch();
  }, [retryFetch]);

  // Handle sort toggle (memoized)
  const handleSort = useCallback((field: keyof Guest) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc');
        return prevField;
      } else {
        setSortDirection('asc');
        return field;
      }
    });
  }, []);

  // Safe access to guests data with type checking and memoization
  const guestData = useMemo(() => {
    return guests?.data || []; 
  }, [guests]);

  // Filter and sort guests with proper null/undefined handling
  const filteredGuests = useMemo(() => {
    if (!guestData || guestData.length === 0) return [];
    
    return guestData.filter((guest: Guest) => {
      // Status filter
      if (statusFilter && guest.status !== statusFilter) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = guest.name?.toLowerCase().includes(searchLower) || false;
        const emailMatch = guest.email?.toLowerCase().includes(searchLower) || false;
        const companyMatch = guest.company?.toLowerCase().includes(searchLower) || false;
        const titleMatch = guest.title?.toLowerCase().includes(searchLower) || false;
        
        return nameMatch || emailMatch || companyMatch || titleMatch;
      }
      
      return true;
    });
  }, [guestData, statusFilter, searchTerm]);

  // Sort filtered guests
  const sortedGuests = useMemo(() => {
    return [...filteredGuests].sort((a, b) => 
      sortData(a, b, sortField, sortDirection)
    );
  }, [filteredGuests, sortField, sortDirection]);

  // Row renderer for virtualized list (memoized)
  const rowRenderer = useCallback(({ index, style }: { index: number, style: React.CSSProperties }) => {
    const guest = sortedGuests[index];
    if (!guest) return null;
    
    return (
      <GuestRow
        guest={guest}
        style={style}
      />
    );
  }, [sortedGuests]);

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 font-heading">Podcast Guests</h1>
            <p className="text-neutral-500 mt-1">
              Manage and schedule podcast guests
            </p>
          </div>
          <Button onClick={handleAddGuest} className="flex items-center gap-2">
            <Plus size={16} />
            <span>Add Guest</span>
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Guest List</CardTitle>
            <CardDescription>
              {sortedGuests.length} guests found
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                <Input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, or company"
                  className="pl-10"
                  aria-label="Search guests"
                />
              </div>
              <div className="w-40">
                <select
                  id="status"
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Filter by status"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="recorded">Recorded</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                  }}
                  className="flex items-center gap-2"
                >
                  <Filter size={16} />
                  <span>Clear Filters</span>
                </Button>
              </div>
            </div>

            {/* Guest List */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 mb-2"></div>
                  <p className="text-gray-500">Loading guests...</p>
                </div>
              ) : error ? (
                <div className="p-8 text-center">
                  <p className="text-red-500 mb-2">Failed to load guests</p>
                  <Button onClick={handleRetry} variant="outline" className="flex items-center gap-2">
                    <RefreshCw size={16} />
                    <span>Retry</span>
                  </Button>
                </div>
              ) : sortedGuests.length > 0 ? (
                <>
                  <div className="grid grid-cols-12 px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div 
                      className="col-span-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
                      onClick={() => handleSort('name')}
                    >
                      <span>Name</span>
                      <ArrowUpDown size={14} />
                    </div>
                    <div 
                      className="col-span-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
                      onClick={() => handleSort('company')}
                    >
                      <span>Company</span>
                      <ArrowUpDown size={14} />
                    </div>
                    <div 
                      className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
                      onClick={() => handleSort('episode_number')}
                    >
                      <span>Episode</span>
                      <ArrowUpDown size={14} />
                    </div>
                    <div 
                      className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
                      onClick={() => handleSort('status')}
                    >
                      <span>Status</span>
                      <ArrowUpDown size={14} />
                    </div>
                    <div className="col-span-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </div>
                  </div>
                  <div style={{ height: Math.min(500, sortedGuests.length * 60) }}>
                    <AutoSizer>
                      {({ height, width }: { height: number, width: number }) => (
                        <List
                          height={height}
                          width={width}
                          itemCount={sortedGuests.length}
                          itemSize={60}
                          overscanCount={5}
                        >
                          {rowRenderer}
                        </List>
                      )}
                    </AutoSizer>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-500">No guests found. Try adjusting your filters or add a new guest.</p>
                  <Button
                    onClick={handleAddGuest}
                    className="mt-4 flex items-center gap-2"
                  >
                    <Plus size={16} />
                    <span>Add Guest</span>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </ErrorBoundary>
  );
};

export default React.memo(GuestList);
